import React, { useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import type { Column } from './types';
import { usePersistentState } from './usePersistentState';

/**
 * ColumnPicker — เลือกคอลัมน์ที่แสดงและจำค่าไว้ต่อผู้ใช้ต่อตาราง
 * คอลัมน์ที่ required = true ปิดไม่ได้ (เช่น รหัสคำขอ) และแสดงเหตุผลด้วย title
 */
export interface ColumnPickerProps<Row> {
  columns: Array<Column<Row>>;
  /** คีย์สำหรับจำค่า เช่น 'claims-queue' */
  persistKey: string;
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}

export function ColumnPicker<Row>({ columns, persistKey, value, onChange, className }: ColumnPickerProps<Row>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [, setStored] = usePersistentState<string[]>(`visible.${persistKey}`, value);

  const set = (ids: string[]) => { onChange(ids); setStored(ids); };

  const toggle = (id: string) => {
    const col = columns.find((c) => c.id === id);
    if (col?.required) return;
    set(value.includes(id) ? value.filter((x) => x !== id) : columns.filter((c) => value.includes(c.id) || c.id === id).map((c) => c.id));
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        onBlur={(e) => { if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
        className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-3)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
      >
        <Icon name="view_column" size={16} />
        คอลัมน์
        <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-600)]">{value.length}/{columns.length}</span>
      </button>

      {open && (
        <div
          role="group"
          aria-label="เลือกคอลัมน์ที่แสดง"
          onBlur={(e) => { if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
          className="absolute right-0 z-[var(--z-dropdown)] mt-[var(--space-1)] max-h-[380px] w-[280px] overflow-auto rounded-[var(--radius-md)] bg-[var(--surface-0)] p-[var(--space-2)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-dropdown)]"
        >
          {columns.map((c) => {
            const checked = value.includes(c.id);
            return (
              <label
                key={c.id}
                title={c.required ? 'คอลัมน์นี้ปิดไม่ได้' : undefined}
                className={cn(
                  'flex min-h-[var(--hit-control)] cursor-pointer items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] hover:bg-[var(--blue-050)]',
                  c.required && 'cursor-not-allowed text-[var(--ink-600)]',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={c.required}
                  onChange={() => toggle(c.id)}
                  className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                />
                <span className="bo-truncate flex-1">{c.header}</span>
                {c.required && <Icon name="lock" size={14} className="text-[var(--ink-400)]" />}
              </label>
            );
          })}
          <div className="mt-[var(--space-2)] flex justify-end border-t border-[var(--line-200)] pt-[var(--space-2)]">
            <button
              type="button"
              onClick={() => set(columns.map((c) => c.id))}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] hover:underline outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              แสดงทุกคอลัมน์
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
